import { DEFAULT_LEVEL_LABELS } from "./skills";

// Collaborateurs : libellés métier, transitions de statut et aides pures (testées dans collaborators.test.js).
// Le serveur reste la seule autorité : ces fonctions ne font que guider l'interface.

export const STATUS_LABELS = {
  pre_registered: "Préinscrit",
  active: "Actif",
  suspended: "Suspendu",
  exited: "Sorti",
};

export const STATUS_BADGE_CLASSES = {
  pre_registered: "bg-slate-100 text-slate-700",
  active: "bg-emerald-50 text-emerald-700",
  suspended: "bg-amber-50 text-amber-700",
  exited: "bg-slate-200 text-slate-600",
};

export const STATUS_FILTER_OPTIONS = Object.entries(STATUS_LABELS).map(([value, label]) => ({ value, label }));

const TRANSITIONS = {
  pre_registered: ["active", "exited"],
  active: ["suspended", "exited"],
  suspended: ["active", "exited"],
  exited: ["active"],
};

const TRANSITION_LABELS = {
  "pre_registered>active": "Marquer comme actif",
  "pre_registered>exited": "Marquer comme sorti (n'a jamais commencé)",
  "active>suspended": "Suspendre",
  "suspended>active": "Reprendre l'activité",
  "active>exited": "Enregistrer la sortie",
  "suspended>exited": "Enregistrer la sortie",
  "exited>active": "Réactiver",
};

export function allowedTransitions(status) {
  return (TRANSITIONS[status] || []).map((to) => ({ to, label: TRANSITION_LABELS[`${status}>${to}`] }));
}

// The everyday next step for a status, shown as a visible button; rarer moves stay in the "⋯" menu.
const PRIMARY_TRANSITION = { pre_registered: "active", suspended: "active", exited: "active" };

export function primaryTransition(status) {
  const to = PRIMARY_TRANSITION[status];
  return to ? { to, label: TRANSITION_LABELS[`${status}>${to}`] } : null;
}

// One sentence explaining a status that needs attention (active needs none).
export function statusExplanation(fiche) {
  if (fiche.status === "pre_registered") return "Cette personne n'a pas encore commencé.";
  if (fiche.status === "suspended") return "L'activité de cette personne est suspendue.";
  if (fiche.status === "exited") return fiche.exit_date ? `Cette personne a quitté Jamora le ${formatDate(fiche.exit_date)}.` : "Cette personne a quitté Jamora.";
  return null;
}

// Contact links: numbers are stored in E.164 (+221771234567) by the server.
const digits = (value) => (value || "").replace(/[^\d+]/g, "");

export function telHref(phone) {
  const d = digits(phone);
  return d ? `tel:${d}` : null;
}

export function whatsappHref(number) {
  const d = digits(number).replace(/^\+/, "");
  return d ? `https://wa.me/${d}` : null;
}

// +221 77 123 45 67 for Senegalese numbers; any other number is shown as stored.
export function formatPhone(phone) {
  const d = digits(phone);
  const sn = /^\+221(\d{2})(\d{3})(\d{2})(\d{2})$/.exec(d);
  return sn ? `+221 ${sn[1]} ${sn[2]} ${sn[3]} ${sn[4]}` : phone || "";
}

// Champs demandés pour un changement de statut : reflète les règles du serveur (dates, motif).
export function statusFormSpec(from, to) {
  const reactivation = from === "exited" && to === "active";
  return {
    entryDate: to === "active" && from !== "suspended",
    exitDate: to === "exited",
    reason: to === "suspended" || to === "exited" || reactivation,
    entryDateLabel: reactivation ? "Date de reprise" : "Date d'entrée",
  };
}

export function isStatusFormValid(from, to, values, today) {
  const spec = statusFormSpec(from, to);
  if (spec.entryDate && (!values.entry_date || values.entry_date > today)) return false;
  if (spec.exitDate && (!values.exit_date || values.exit_date > today)) return false;
  if (spec.reason && (values.reason || "").trim().length < 3) return false;
  return true;
}

export function buildStatusPayload(from, to, values, expectedVersion) {
  const spec = statusFormSpec(from, to);
  const payload = { expected_version: expectedVersion, to };
  if (spec.entryDate) payload.entry_date = values.entry_date;
  if (spec.exitDate) payload.exit_date = values.exit_date;
  if (spec.reason) payload.reason = values.reason.trim();
  return payload;
}

export const MIN_OVERRIDE_REASON = 10;
export const MIN_CORRECTION_REASON = 10; // a correction rewrites a recorded fact: the reason must say why

// Dates of the CURRENT period, corrected by an administrator (the server re-checks every rule, including the earlier periods).
export function canCorrectDates(fiche, user) {
  return user?.role === "admin" && !!fiche && !fiche.archived && (fiche.status === "active" || fiche.status === "suspended" || fiche.status === "exited");
}

export function isDatesCorrectionValid(fiche, values, today) {
  const entry = values.entry_date || "";
  const exit = values.exit_date || "";
  if (!entry || entry > today) return false;
  if (fiche.status === "exited" && (!exit || exit > today || exit < entry)) return false;
  if (!changedDates(fiche, values).length) return false;
  return (values.reason || "").trim().length >= MIN_CORRECTION_REASON;
}

export function changedDates(fiche, values) {
  return ["entry_date", "exit_date"].filter((key) => (key === "exit_date" ? fiche.status === "exited" : true) && (values[key] || null) !== (fiche[key] || null));
}

// Only the dates that really changed are sent, with the reason.
export function buildDatesCorrectionPayload(fiche, values) {
  const payload = { expected_version: fiche.version, reason: values.reason.trim() };
  for (const key of changedDates(fiche, values)) payload[key] = values[key];
  return payload;
}

export function isOverrideValid(confirmed, reason) {
  return !!confirmed && (reason || "").trim().length >= MIN_OVERRIDE_REASON;
}

// L'API renvoie soit une chaîne, soit { code, msg, ... } pour les refus métier.
export function errorInfo(error) {
  const detail = error?.response?.data?.detail;
  if (detail && typeof detail === "object" && !Array.isArray(detail)) return { status: error.response.status, code: detail.code || null, data: detail };
  return { status: error?.response?.status ?? null, code: null, data: null };
}

const DUPLICATE_CODES = ["duplicate_warning", "duplicate_blocking", "duplicate_hidden", "override_reason_required"];

export function isDuplicateRefusal(info) {
  return info.status === 409 && DUPLICATE_CODES.includes(info.code);
}

const DUPLICATE_CRITERIA_LABELS = { name: "même nom", email: "même email", phone: "même numéro" };

export function describeMatch(match) {
  const reasons = (match.criteria || []).map((c) => DUPLICATE_CRITERIA_LABELS[c] || c).join(", ");
  return reasons ? `${match.display_name} (${reasons})` : match.display_name;
}

// Clé d'idempotence : une par ouverture de formulaire (une création réussie la consomme, un refus non).
export function newIdempotencyKey() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID().replace(/-/g, "") + "ab";
  return Array.from({ length: 34 }, () => Math.floor(Math.random() * 36).toString(36)).join("");
}

// Corps d'un PATCH : uniquement ce qui a réellement changé (le serveur ignore le reste mais l'historique, lui, le verrait).
export function buildUpdatePayload(original, draft, expectedVersion) {
  const payload = { expected_version: expectedVersion };
  for (const key of ["first_name", "last_name", "relation_type", "job_title"]) {
    if ((draft[key] || "") !== (original[key] || "")) payload[key] = (draft[key] || "").trim() || null;
  }
  if ((draft.service_id || null) !== (original.service_id || null)) payload.service_id = draft.service_id || null;
  // the manager is a fiche OR a CRM account: only one is ever sent, and clearing sends manager_id: null (the server replaces the whole designation)
  if ((draft.manager_id || null) !== (original.manager_id || null) || (draft.manager_user_id || null) !== (original.manager_user_id || null)) {
    if (draft.manager_user_id) payload.manager_user_id = draft.manager_user_id;
    else payload.manager_id = draft.manager_id || null;
  }
  // administrator only (the server refuses anyone else); the screen only offers the switch to an administrator
  if (!!draft.top_level !== !!original.top_level) payload.top_level = !!draft.top_level;
  for (const key of ["zone_ids", "job_function_ids"]) {
    const before = [...(original[key] || [])].sort().join(",");
    const after = [...(draft[key] || [])].sort().join(",");
    if (before !== after) payload[key] = draft[key] || [];
  }
  const contacts = {};
  for (const key of ["email", "phone", "whatsapp"]) {
    const before = (original.contacts || {})[key] || "";
    const after = ((draft.contacts || {})[key] || "").trim();
    if (before !== after) contacts[key] = after || null;
  }
  if (Object.keys(contacts).length) payload.contacts = contacts;
  return payload;
}

export function hasChanges(payload) {
  return Object.keys(payload).some((key) => key !== "expected_version");
}

// Fiche de l'utilisateur lui-même : le serveur interdit ces champs (sauf admin) ; l'interface les grise.
const SELF_LOCKED_FIELDS = ["relation_type", "job_title", "job_function_ids", "service_id", "manager_id"];

export function isFieldLocked(field, fiche, user) {
  return !!fiche?.is_me && user?.role !== "admin" && SELF_LOCKED_FIELDS.includes(field);
}

export function formatDate(iso) {
  if (!iso) return "Non renseignée";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
}

const HISTORY_KIND_LABELS = {
  created: "Fiche créée",
  updated: "Fiche modifiée",
  status_changed: "Changement de statut",
  archived: "Fiche archivée",
  unarchived: "Fiche désarchivée",
  user_linked: "Compte CRM rattaché",
  user_unlinked: "Compte CRM détaché",
  duplicate_override: "Doublon confirmé",
  skill_level_set: "Niveau de compétence",
  skill_level_corrected: "Niveau de compétence corrigé",
  dates_corrected: "Dates corrigées",
  responsibility_moved: "Responsable changé (passation)",
  handover_completed: "Passation des responsabilités",
  accreditation_granted: "Habilitation accordée",
  accreditation_revoked: "Habilitation révoquée",
  mandate_created: "Mandat légal enregistré",
  mandate_updated: "Mandat légal corrigé",
  mandate_ended: "Mandat légal terminé",
  mandate_verified: "Mandat légal vérifié",
  mandate_unverified: "Vérification du mandat retirée",
};

export const FIELD_LABELS = { name: "nom", contacts: "coordonnées", relation_type: "type de relation", job_title: "fonction", job_function_ids: "fonctions métier", service_id: "service", manager_id: "responsable", manager_user_id: "responsable", zone_ids: "zones", top_level: "sommet de l'organisation" };

// `levelLabel` : libellés des niveaux de compétence tels que configurés (à défaut, les libellés d'origine).
export function describeHistoryEntry(entry, levelLabel = (code) => DEFAULT_LEVEL_LABELS[code] || code) {
  const base = HISTORY_KIND_LABELS[entry.kind] || entry.kind;
  if (entry.kind === "skill_level_set" && entry.data?.to) {
    const from = entry.data.from ? `${levelLabel(entry.data.from)} → ` : "";
    return `${base} « ${entry.data.skill_label} » : ${from}${levelLabel(entry.data.to)}`;
  }
  if ((entry.kind === "accreditation_granted" || entry.kind === "accreditation_revoked") && entry.data?.skill_label) {
    return `${base} : ${entry.data.skill_label} (${levelLabel(entry.data.level)})`;
  }
  if (entry.kind === "skill_level_corrected" && entry.data?.to) {
    const from = entry.data.from ? `${levelLabel(entry.data.from)} → ` : "";
    return `${base} « ${entry.data.skill_label} » : ${from}${levelLabel(entry.data.to)}`;
  }
  if (entry.kind === "dates_corrected" && entry.data) {
    const parts = [["entry_date", "entrée"], ["exit_date", "sortie"]].filter(([key]) => entry.data[key]).map(([key, label]) => `${label} ${formatDate(entry.data[key].from)} → ${formatDate(entry.data[key].to)}`);
    return parts.length ? `${base} : ${parts.join(", ")}` : base;
  }
  if (entry.kind === "handover_completed" && entry.data) {
    const people = entry.data.reports || 0;
    const services = entry.data.services || 0;
    return `${base} : ${people} personne${people > 1 ? "s" : ""}, ${services} service${services > 1 ? "s" : ""}`;
  }
  if (entry.kind === "status_changed" && entry.data?.to) {
    return `${base} : ${STATUS_LABELS[entry.data.from] || entry.data.from} → ${STATUS_LABELS[entry.data.to] || entry.data.to}`;
  }
  if (entry.kind === "updated" && entry.data?.fields?.length) {
    return `${base} (${entry.data.fields.map((f) => FIELD_LABELS[f] || f).join(", ")})`;
  }
  return base;
}

// Périodes passées et en cours d'une personne, reconstituées depuis l'historique complet (aucune troncature côté serveur).
export function periodsFromHistory(entries, fiche) {
  const periods = [];
  for (const entry of entries) {
    if (entry.kind === "status_changed" && entry.data?.previous_period) periods.push(entry.data.previous_period);
  }
  periods.sort((a, b) => (a.entry_date || "").localeCompare(b.entry_date || ""));
  if (fiche?.entry_date) periods.push({ entry_date: fiche.entry_date, exit_date: fiche.exit_date || null, current: true });
  return periods;
}

export const ACCOUNT_STATE_LABELS = { active: "Compte actif", disabled: "Compte désactivé", missing: "Compte introuvable" };

// --- Organisation: the referentials of the directory, each one a page of the « Collaborateurs » application's menu ---------------------
// `tab` is the historical key (`?tab=` links, bookmarks and the Copilote's answers keep working: they redirect to the page of the section).
export const ORG_SECTIONS = [
  { tab: "services", slug: "services", label: "Services", description: "Les services de l'entreprise, leur chef et leur adjoint.", testid: "nav-org-services" },
  { tab: "zone", slug: "zones", label: "Zones", description: "Les zones où les personnes interviennent.", testid: "nav-org-zones" },
  { tab: "relation_type", slug: "types-de-relation", label: "Types de relation", description: "Salarié, prestataire, partenaire… ce qui lie une personne à l'entreprise.", testid: "nav-org-types" },
  { tab: "job_functions", slug: "fonctions-metier", label: "Fonctions métier", description: "Les fonctions exercées et les compétences attendues pour chacune.", testid: "nav-org-job-functions" },
  { tab: "skills", slug: "competences", label: "Compétences", description: "Le référentiel des compétences évaluées sur les fiches.", testid: "nav-org-skills" },
  { tab: "levels", slug: "niveaux", label: "Niveaux", description: "Les niveaux de maîtrise d'une compétence.", testid: "nav-org-levels" },
  { tab: "mission_types", slug: "types-de-mission", label: "Types de mission", description: "Les types de mission, leurs compétences et leurs procédures.", testid: "nav-org-mission-types", missions: true },
  { tab: "report_templates", slug: "modeles-compte-rendu", label: "Modèles de compte rendu", description: "Les modèles utilisés pour rédiger les comptes rendus de mission.", testid: "nav-org-report-templates", missions: true, manageTypes: true },
  { tab: "mandate_types", slug: "types-de-mandat", label: "Types de mandat", description: "Les mandats légaux (gérant…) que l'administrateur peut confier à une personne.", testid: "nav-org-mandate-types", adminOnly: true },
  { tab: "consistency", slug: "controle", label: "Contrôle de cohérence", description: "Les anomalies de l'organisation : responsables partis, services sans chef, boucles…", testid: "nav-org-consistency", adminOnly: true },
];
export const ORG_BASE = "/collaborateurs/organisation";
export const orgPath = (slug) => `${ORG_BASE}/${slug}`;
export const orgSectionBySlug = (slug) => ORG_SECTIONS.find((s) => s.slug === slug) || null;
export const orgSectionByTab = (tab) => ORG_SECTIONS.find((s) => s.tab === tab) || null;

// The sections a person may open: the mission ones need the missions module and its rights (same rule the old tabs followed).
export function visibleOrgSections(user, can) {
  const showMissions = !!user?.features?.missions && can(["missions:manage_types", "missions:view", "missions:view_all", "missions:manage", "missions:manage_all"]);
  const manageTypes = can("missions:manage_types");
  const isAdmin = user?.role === "admin";
  return ORG_SECTIONS.filter((s) => (!s.missions || showMissions) && (!s.manageTypes || manageTypes) && (!s.adminOnly || isAdmin));
}

// Headcount of a service, shown only when the server sent it (callers who see every fiche): « 3 personnes », « 1 personne », « Aucune personne ».
export function memberCountLabel(count) {
  if (count === undefined || count === null) return null;
  if (count === 0) return "Aucune personne";
  return `${count} ${count > 1 ? "personnes" : "personne"}`;
}

// What deactivating a service means for the people still attached to it (they stay attached, the service stops receiving new people).
export function serviceDeactivationWarning(service) {
  const count = service?.member_count;
  if (!service?.active || !count) return null;
  return `${count} ${count > 1 ? "personnes y sont encore rattachées" : "personne y est encore rattachée"} : ${count > 1 ? "elles" : "elle"} ${count > 1 ? "restent" : "reste"} dans ce service, qui ne pourra plus accueillir personne de nouveau.`;
}

// Short line of a directory row on a phone: the job, then the service (the reference and the relation type stay on the fiche).
export function mobileRowSummary(c, serviceName) {
  return [c.job_title, serviceName(c.service_id)].filter(Boolean).join(" · ");
}

// Parameters of the directory request for the « manager » filter: a fiche, or an account that has none (the two are exclusive).
export function managerFilterParams(manager) {
  if (!manager?.id) return {};
  return manager.kind === "account" ? { manager_user_id: manager.id } : { manager_id: manager.id };
}
